import { formatPhoneNumber } from 'util/formatPhoneNumber';
import { Box, Typography } from '@mui/material';
import React, { ReactElement } from 'react';
import { deriveLeaderUiStatus, OrgMemberRow } from './deriveLeaderUiStatus';
import { formatLeaderDate } from './formatLeaderDates';
import { LeadershipStatusPill } from './LeadershipStatusPill';
import { LeadershipTableRowActions } from './LeadershipTableRowActions';

const COL_NAME = 300;
const COL_STATUS = 190;
const COL_ROLE = 250;
const COL_DATE = 100;
const COL_LAST_SEEN = 100;
const GAP = 48;

const TEXT_COLOR = '#383838';
const HEADER_BORDER = '#E5E7EB';
const ROW_BORDER = '#DEDEDE';

function HeaderCell ({ children, width }: { children: React.ReactNode; width: number }): ReactElement {
  return (
    <Box
      sx={{
        width,
        flexShrink: 0,
        py: '16px',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Typography
        sx={{
          fontFamily: '\'Poppins\', sans-serif',
          fontSize: '12px',
          fontWeight: 600,
          lineHeight: '18px',
          letterSpacing: 0,
          textTransform: 'uppercase',
          color: TEXT_COLOR,
          whiteSpace: 'nowrap',
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

export function OrganizationLeadersDesktopTable ({
  leaders,
  canRemoveAdmin,
  onEdit,
  onRemove,
  onCancel,
  onReInvite,
}: {
  leaders: OrgMemberRow[];
  canRemoveAdmin?: boolean;
  onEdit: (leader: OrgMemberRow) => void;
  onRemove: (leader: OrgMemberRow) => void;
  onCancel: (leader: OrgMemberRow) => void;
  onReInvite: (leader: OrgMemberRow) => void;
}): ReactElement {
  return (
    <Box sx={{ width: '100%', maxWidth: 1400, overflow: 'auto' }}>
      {/* Header Row */}
      <Box
        display="flex"
        alignItems="center"
        gap={`${GAP}px`}
        sx={{
          borderBottom: `1px solid ${HEADER_BORDER}`,
        }}
      >
        <HeaderCell width={COL_NAME}>Name</HeaderCell>
        <HeaderCell width={COL_STATUS}>Status</HeaderCell>
        <HeaderCell width={COL_ROLE}>Role</HeaderCell>
        <HeaderCell width={COL_DATE}>Date Joined</HeaderCell>
        <HeaderCell width={COL_LAST_SEEN}>Last Seen</HeaderCell>
      </Box>

      {/* Body Rows */}
      {leaders.map(leader => {
        const ui = deriveLeaderUiStatus(leader);
        const phone = leader.phoneNumber && formatPhoneNumber(leader.phoneNumber);

        return (
          <Box
            key={leader.id}
            display="flex"
            alignItems="center"
            gap={`${GAP}px`}
            sx={{
              py: '16px',
              borderBottom: `1px solid ${ROW_BORDER}`,
              '&:last-of-type': { borderBottom: 'none' },
            }}
          >
            {/* NAME */}
            <Box
              sx={{
                width: COL_NAME,
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Typography
                sx={{
                  fontFamily: '\'Poppins\', sans-serif',
                  fontSize: '16px',
                  fontWeight: 500,
                  lineHeight: '24px',
                  color: TEXT_COLOR,
                }}
              >
                {leader.firstName} {leader.lastName}
              </Typography>
              <Typography
                sx={{
                  fontFamily: '\'Poppins\', sans-serif',
                  fontSize: '12px',
                  fontWeight: 400,
                  lineHeight: '18px',
                  color: TEXT_COLOR,
                }}
              >
                {leader.schoolEmail}
              </Typography>
              {phone ? (
                <Typography
                  sx={{
                    fontFamily: '\'Poppins\', sans-serif',
                    fontSize: '12px',
                    fontWeight: 400,
                    lineHeight: '18px',
                    color: TEXT_COLOR,
                  }}
                >
                  {phone}
                </Typography>
              ) : null}
            </Box>

            {/* STATUS */}
            <Box
              sx={{
                width: COL_STATUS,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <LeadershipStatusPill status={ui} />
            </Box>

            {/* ROLE */}
            <Typography
              sx={{
                width: COL_ROLE,
                flexShrink: 0,
                fontFamily: '\'Poppins\', sans-serif',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '21px',
                color: TEXT_COLOR,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {leader.orgRole || '—'}
            </Typography>

            {/* DATE JOINED */}
            <Box sx={{ width: COL_DATE, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
              <Typography
                sx={{
                  fontFamily: '\'Poppins\', sans-serif',
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '21px',
                  color: TEXT_COLOR,
                  whiteSpace: 'nowrap',
                }}
              >
                {formatLeaderDate(leader.createdAt)}
              </Typography>
            </Box>

            {/* LAST SEEN */}
            <Box sx={{ width: COL_LAST_SEEN, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
              <Typography
                sx={{
                  fontFamily: '\'Poppins\', sans-serif',
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '21px',
                  color: TEXT_COLOR,
                  whiteSpace: 'nowrap',
                }}
              >
                {formatLeaderDate(leader.lastSeen)}
              </Typography>
            </Box>

            {/* ACTIONS */}
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <LeadershipTableRowActions
                leader={leader}
                canRemoveAdmin={canRemoveAdmin}
                onEdit={onEdit}
                onRemove={onRemove}
                onCancel={onCancel}
                onReInvite={onReInvite}
              />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
